import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { asset } from "@/lib/assets";
const notificationCenter = asset(
  "case-studies/notification/notificationcenter.png",
);
const notificationCenter2 = asset(
  "case-studies/notification/notificationcenter2.png",
);
const headerImg = asset("case-studies/notification/LiveActivitiesHeader.png");
const appBeforeImg = asset("case-studies/notification/appbeforeredesign.png");
const clusterImg = asset("case-studies/notification/Cluster.png");
const iconsImg = asset("case-studies/notification/Icons.png");
const channelsImg = asset(
  "case-studies/notification/notification-channels.png",
);
const statusImg = asset("case-studies/notification/Status.png");
const anatomyImg = asset("case-studies/notification/Anatomy.png");
const resultImg = asset("case-studies/notification/Result.png");
const system1Img = asset("case-studies/notification/system1.png");
const system2Img = asset("case-studies/notification/system2.png");
const system3Img = asset("case-studies/notification/system3.png");
const emailsImg = asset("case-studies/notification/Emails.png");
const usabilityImg = asset("case-studies/notification/usertestingresult.png");

// ─── Image components ────────────────────────────────────────────────────────

function ImageSlot({
  src,
  alt,
  className = "",
  caption,
  fit = "cover",
}: {
  src: string;
  alt: string;
  className?: string;
  caption?: string;
  fit?: "cover" | "contain";
}) {
  const [lightbox, setLightbox] = useState(false);

  return (
    <>
      <div className="w-full">
        <div
          className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl overflow-hidden flex items-center justify-center cursor-zoom-in ${className}`}
          onClick={() => setLightbox(true)}
          title="Click to expand"
        >
          <img
            src={src}
            alt={alt}
            className={`w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"}`}
            onError={(e) => {
              const el = e.currentTarget as HTMLImageElement;
              el.style.display = "none";
              const parent = el.parentElement;
              if (parent && !parent.querySelector(".placeholder-label")) {
                const label = document.createElement("p");
                label.className =
                  "placeholder-label text-xs text-[#888888] text-center p-4";
                label.textContent = alt;
                parent.appendChild(label);
              }
            }}
          />
        </div>
        {caption && (
          <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">
            {caption}
          </p>
        )}
      </div>

      {lightbox &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setLightbox(false)}
          >
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setLightbox(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

function PlaceholderSlot({
  label,
  className = "",
  caption,
}: {
  label: string;
  className?: string;
  caption?: string;
}) {
  return (
    <div className="w-full">
      <div
        className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl flex items-center justify-center ${className}`}
      >
        <p className="text-xs text-[#888888] text-center p-6">{label}</p>
      </div>
      {caption && (
        <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">
          {caption}
        </p>
      )}
    </div>
  );
}

// ─── Side nav ────────────────────────────────────────────────────────────────

const NAV_SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "approach", label: "Approach" },
  { id: "result", label: "Result" },
  { id: "usability-testing", label: "Usability Testing" },
  { id: "impact", label: "Impact" },
  { id: "key-takeaway", label: "Key Takeaway" },
];

function SideNav() {
  const [active, setActive] = useState<string>(NAV_SECTIONS[0].id);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visibleSections = new Map<string, number>();

    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            visibleSections.set(id, entry.intersectionRatio);
          } else {
            visibleSections.delete(id);
          }
          if (visibleSections.size > 0) {
            const topmost = [...visibleSections.entries()].reduce((a, b) =>
              a[1] >= b[1] ? a : b,
            );
            setActive(topmost[0]);
          }
        },
        { threshold: [0, 0.25, 0.5], rootMargin: "-10% 0px -60% 0px" },
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <nav className="hidden xl:flex flex-col gap-1 sticky top-32 self-start w-48 shrink-0">
      {/* Project card */}
      <div className="mb-6 pb-6 border-b border-[#E2E8F0]">
        <p className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] text-sm leading-tight mb-0.5">
          Cashi
        </p>
        <p className="text-xs text-[#888888] leading-snug">
          Digital wallet app
          <br />
          Walmart Mexico
        </p>
      </div>

      {NAV_SECTIONS.map(({ id, label }) => (
        <a
          key={id}
          href={`#${id}`}
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById(id)
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className={`text-xs font-medium py-1.5 pl-3 border-l-2 transition-all duration-200 ${
            active === id
              ? "border-[#1D3FE8] text-[#1A1A1A]"
              : "border-transparent text-[#888888] hover:text-[#1A1A1A] hover:border-[#E2E8F0]"
          }`}
        >
          {label}
        </a>
      ))}
    </nav>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function CaseStudyNotification() {
  return (
    <div className="min-h-screen bg-[#F7F8FA] font-[DM_Sans]">
      {/* Sticky header */}
      <header className="sticky top-0 z-50 bg-[#F7F8FA]/90 backdrop-blur-sm border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
          <Link
            to="/#work"
            className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
          >
            ← All work
          </Link>
          <Link
            to="/"
            className="font-[Bricolage_Grotesque] font-bold text-sm text-[#1A1A1A]"
          >
            Verónica <span className="text-[#1D3FE8]">Aguilar</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12 pt-24 pb-20">
        <AnimatedSection>
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#888888] mb-4">
            Cashi · Digital wallet app (Walmart Mexico)
          </p>
          <h1 className="font-[Bricolage_Grotesque] font-extrabold tracking-[-0.04em] text-[clamp(2.25rem,5vw,3.75rem)] text-[#1A1A1A] leading-[1.05] mb-6">
            Notification center for{" "}
            <span className="text-[#1D3FE8]">a digital wallet.</span>
          </h1>
          <p className="text-[#555] text-lg leading-[1.8] max-w-[680px] mb-12">
            As part of a full app redesign, we introduced a notification center
            to give users clear, real-time visibility into their transactions.
            This addressed a key trust gap and created a scalable system for
            communication across the product.
          </p>

          {/* Meta strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8 border-t border-[#E2E8F0]">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                Role
              </p>
              <p className="text-[0.9375rem] font-medium text-[#1A1A1A] leading-relaxed">
                Lead the design strategy, execution for the notification
                experience, and contributed to the overall UI rev-vamp of the
                app.
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                Team
              </p>
              <p className="text-[0.9375rem] font-medium text-[#1A1A1A] leading-relaxed">
                UX Designer · UX Writer · UI/Visual Designer (me) · Product
                Manager · Engineers
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* Header image */}
      <AnimatedSection>
        <div className="max-w-5xl mx-auto px-6 sm:px-12 pb-20">
          <ImageSlot
            src={headerImg}
            alt="Notification center header"
            className="w-full aspect-[16/7]"
            caption="Notification center — final design"
          />
        </div>
      </AnimatedSection>

      {/* Sections */}
      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        <div className="flex gap-16 items-start">
          <SideNav />
          <div className="flex-1 min-w-0">
            {/* Overview */}
            <AnimatedSection
              id="overview"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Overview
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                A wallet without feedback is a wallet without trust.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                Cashi is a digital wallet that helps users pay for groceries,
                utility bills, and digital services in one place. As part of the
                app revamp, we conducted guerrilla testing and observed users
                interacting with the existing product. A key insight emerged:
              </p>

              {/* Yellow callout */}
              <div className="bg-[#F5C542] rounded-2xl px-6 py-5 mb-8 max-w-[640px]">
                <p className="text-[#1A1A1A] font-semibold text-[0.9375rem] leading-[1.7]">
                  Users lacked a sense of control and transparency over their
                  transactions.
                </p>
              </div>

              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                Participants consistently expected communication for every
                transaction. Without it, they felt unsure about their money and
                hesitant to trust the product with financial and personal
                information.
              </p>

              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6">
                The existing experience did not provide:
              </p>
              <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10 space-y-1 pl-1">
                <li>Transaction feedback</li>
                <li>Clear status visibility</li>
                <li>A centralized place to track activity</li>
              </ul>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10">
                This resulted in uncertainty, missed updates, and reduced
                confidence in the app.
              </p>

              <ImageSlot
                src={appBeforeImg}
                alt="App before the redesign"
                className="w-full aspect-[16/8]"
                caption="App before the redesign"
              />
            </AnimatedSection>

            {/* Approach */}
            <AnimatedSection
              id="approach"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Approach
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                A system design challenge, not just a UI feature.
              </h2>

              {/* Step 1 */}
              <div className="mb-12">
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  1. Content mapping
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6">
                  We mapped all user actions that could trigger notifications
                  and organized them using an affinity diagram. This helped us:
                </p>
                <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8 space-y-1 pl-1">
                  <li>Identify key moments that require communication</li>
                  <li>Cluster notifications into four main categories</li>
                  <li>Define iconography/logos for actions</li>
                </ul>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ImageSlot
                    src={clusterImg}
                    alt="Affinity diagram clusters"
                    className="aspect-[4/3]"
                    caption="Affinity diagram — notification clusters"
                    fit="contain"
                  />
                  <ImageSlot
                    src={iconsImg}
                    alt="Category icons"
                    className="aspect-[4/3]"
                    caption="Iconography by category"
                    fit="contain"
                  />
                </div>
              </div>

              {/* Step 2 */}
              <div className="mb-12">
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  2. Structuring the system
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6">
                  From the content mapping, we defined:
                </p>
                <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8 space-y-1 pl-1">
                  <li>Channels for each type of communication</li>
                  <li>
                    Rules for which notifications can be customized by the user
                  </li>
                  <li>Status indicators</li>
                </ul>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                  We worked closely with engineering to ensure feasibility and
                  scalability.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ImageSlot
                    src={channelsImg}
                    alt="Communication channels"
                    className="aspect-[4/3]"
                    caption="Communication channels per notification type"
                    fit="contain"
                  />
                  <ImageSlot
                    src={statusImg}
                    alt="Status indicators"
                    className="aspect-[4/3]"
                    caption="Status indicator system"
                    fit="contain"
                  />
                </div>
              </div>

              {/* Step 3 */}
              <div>
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  3. Notification component
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6">
                  My goal for the component was to use visual elements to make
                  notification types recognizable at a glance, and reduce
                  reliance on reading. I focused on:
                </p>
                <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6 space-y-1 pl-1">
                  <li>Status indicators (success, error, warning)</li>
                  <li>Iconography/logos for quick identification</li>
                  <li>Consistent anatomy for all notifications</li>
                  <li>Visual hierarchy to prioritize information</li>
                  <li>Supporting text for clarity</li>
                </ul>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-3">
                  This created a clear structure:
                </p>
                <div className="flex items-center gap-3 mb-10 flex-wrap">
                  {["Status", "Category", "Message", "Action"].map(
                    (step, i, arr) => (
                      <div key={step} className="flex items-center gap-3">
                        <span className="bg-[#1D3FE8] text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                          {step}
                        </span>
                        {i < arr.length - 1 && (
                          <span className="text-[#888888]">→</span>
                        )}
                      </div>
                    ),
                  )}
                </div>
                <ImageSlot
                  src={anatomyImg}
                  alt="Component anatomy"
                  className="w-full aspect-[16/7]"
                  caption="Notification component anatomy"
                  fit="contain"
                />
              </div>
            </AnimatedSection>

            {/* Result */}
            <AnimatedSection
              id="result"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Result
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                One center for everything that matters.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-4">
                The final solution was a notification center + system that:
              </p>
              <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10 space-y-1 pl-1">
                <li>Centralized all transaction updates</li>
                <li>Made status instantly recognizable</li>
                <li>Reduced cognitive load through consistent patterns</li>
                <li>Enabled customizable notification configuration</li>
              </ul>

              <div className="mb-16">
                <ImageSlot
                  src={resultImg}
                  alt="Notification center result"
                  className="w-full aspect-[16/8]"
                  caption="Notification center — final result"
                />
              </div>

              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                Once defined, I translated the system into our component
                library:
              </p>
              <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10 space-y-1 pl-1">
                <li>Notification anatomy</li>
                <li>Badge system</li>
                <li>Status indicators</li>
                <li>Copy guidelines</li>
                <li>Page templates</li>
              </ul>

              <div className="flex flex-col gap-4 mb-16">
                <ImageSlot
                  src={system1Img}
                  alt="Design system — notification anatomy"
                  className="w-full aspect-[16/6]"
                  caption="Component library — notification anatomy"
                  fit="contain"
                />
                <ImageSlot
                  src={system2Img}
                  alt="Design system — badge system & status indicators"
                  className="w-full aspect-[16/6]"
                  caption="Component library — badge system & status indicators"
                  fit="contain"
                />
                <ImageSlot
                  src={system3Img}
                  alt="Design system — copy guidelines & page templates"
                  className="w-full aspect-[16/6]"
                  caption="Component library — copy guidelines & page templates"
                  fit="contain"
                />
              </div>

              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6">
                The visual system extended beyond the notification center into:
              </p>
              <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8 space-y-1 pl-1">
                <li>Email communications</li>
                <li>Filters and categorization across the product</li>
              </ul>
              <ImageSlot
                src={emailsImg}
                alt="Email communications"
                className="w-full aspect-[16/7]"
                caption="Email communication templates"
                fit="contain"
              />
            </AnimatedSection>

            {/* Usability Testing */}
            <AnimatedSection
              id="usability-testing"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Usability Testing
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                Validated with real users.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                We validated the experience through usability testing. Key
                results:
              </p>

              <div className="flex flex-col gap-6 mb-12">
                {[
                  "Participants understood their transaction status without assistance",
                  "Participants found notifications easier to scan and prioritize",
                  "Participants reported increased confidence in understanding their financial activity",
                ].map((result, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <span className="mt-1 text-[#1D3FE8] font-[Bricolage_Grotesque] font-bold text-sm w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[#444] text-[0.9375rem] leading-[1.85]">
                      {result}
                    </p>
                  </div>
                ))}
              </div>

              <ImageSlot
                src={usabilityImg}
                alt="Usability testing results"
                className="w-full aspect-[16/7]"
                caption="Usability testing results"
              />
            </AnimatedSection>

            {/* Impact */}
            <AnimatedSection
              id="impact"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Impact
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                One of many pieces that made the redesign work.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                This was one project of many that made the whole redesign. I was
                happy to lead the strategy and execution of this.
              </p>
              <div className="flex flex-col gap-6">
                {[
                  "Established a scalable communication system across the app",
                  "Improved clarity and trust in transaction-related interactions",
                  "Enabled cleaner, faster implementation through a structured component system",
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <span className="mt-1 text-[#1D3FE8] font-[Bricolage_Grotesque] font-bold text-sm w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[#444] text-[0.9375rem] leading-[1.85]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            {/* Key Takeaway */}
            <AnimatedSection id="key-takeaway" className="py-20">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Key Takeaway
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                Designing for financial products means designing for trust.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10">
                This project reinforced that:
              </p>
              <div className="flex flex-col gap-6">
                {[
                  {
                    label: "UI doesn't need to be flashy to be impactful",
                    body: "Clear status, consistent hierarchy, and a centralized place for updates can move the needle on trust more than any visual flourish.",
                  },
                  {
                    label: "Systems thinking creates long-term value",
                    body: "Solving this as a system — not just a screen — meant the patterns extended naturally into email, filters, and future product areas.",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <span className="mt-1 text-[#1D3FE8] font-[Bricolage_Grotesque] font-bold text-sm w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] mb-2">
                        {item.label}
                      </p>
                      <p className="text-[#555] text-[0.9375rem] leading-[1.85]">
                        {item.body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            {/* Footer nav */}
            <div className="border-t border-[#E2E8F0] py-16 flex items-center justify-between">
              <Link
                to="/#work"
                className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
              >
                ← All case studies
              </Link>
              <div className="text-right">
                <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                  Next case study
                </p>
                <Link
                  to="/case-study/design-system"
                  className="font-[Bricolage_Grotesque] font-extrabold text-lg tracking-[-0.02em] text-[#1A1A1A] hover:text-[#1D3FE8] transition-colors"
                >
                  One source of truth →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
